/*
 * Copyright (c) 2023, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */
import { History, LocationDescriptor, createBrowserHistory } from 'history';
import { useCallback, useMemo } from 'react';
import { Router } from 'react-router-dom';

interface CustomRouterProps {
  children: React.ReactNode;
}
export default function CustomRouter(props: CustomRouterProps) {
  const { children } = props;

  /**
   * Interceptor that is called in every route call.
   * @param originalPath request path or object with path details
   * @returns {String} returns the updated path
   */
  const pathInterceptor = useCallback((originalPath: LocationDescriptor) => {
    let path = '';
    let queryStringsRaw = '';
    if (typeof originalPath === 'object' && originalPath !== null) {
      path = originalPath.pathname ? originalPath.pathname : '';
      queryStringsRaw = originalPath.search ? originalPath.search : '';
    } else {
      [path, queryStringsRaw] = originalPath.split('?');
    }
    const queryParams = new URLSearchParams(queryStringsRaw);
    return `${path}?${queryParams.toString()}`;
  }, []);

  /**
   * Overrides the default push and replace functions of history
   * object with a custom function using path interceptor
   * @param originalHistory original browser history object
   * @returns {History} updated history object
   */
  const historyEnhancer = (originalHistory: History) => ({
    ...originalHistory,
    push: (path: LocationDescriptor, state?: unknown) =>
      originalHistory.push(pathInterceptor(path), state),
    replace: (path: LocationDescriptor, state?: unknown) =>
      originalHistory.replace(pathInterceptor(path), state),
  });

  const history = useMemo(() => createBrowserHistory(), []);

  const customHistory = useMemo(() => historyEnhancer(history), []);

  return <Router history={customHistory}>{children}</Router>;
}
